import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { useFlag } from '@unleash/proxy-client-react'
import { getAuth } from 'firebase/auth'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BucketsByUserIdDocument,
  BucketType,
  BucketUserConvertDocument,
  BucketUserMigrateDocument,
  ProfileMigrationStatus,
  UserLoginDocument
} from '../../../../graphql/generated'
import { modalVar } from '../../../../providers/apollo/cache'
import logger from '../../../../utils/logger'
import useLoginFirebase from '../../../auth/hooks/useLogin'
import useUserProfileUpdate from '../../hooks/useUserProfileUpdate'

export default function TermsAndConditionsLogin() {
  const modal = useReactiveVar(modalVar)
  const { attemptRedirect } = useLoginFirebase()
  const { mutation, loading } = useUserProfileUpdate()
  const [legacyLoginMutation] = useMutation(UserLoginDocument)
  const [getBucketsQuery] = useLazyQuery(BucketsByUserIdDocument, { nextFetchPolicy: 'network-only' })
  const [userMigrateMutation] = useMutation(BucketUserMigrateDocument)
  const [userConvertMutation] = useMutation(BucketUserConvertDocument)
  const [showCancel, setShowCancel] = useState(false)
  const useNewMigrationMutation = useFlag('useNewMigrationMutation')
  const onDemandConversionEnabled = useFlag('onDemandConversionEnabled')

  const decline = () => {
    setShowCancel(true)
  }

  const startMigrationOrConversion = async () => {
    const { uid: userId } = getAuth().currentUser!

    const { data } = await getBucketsQuery({
      variables: { userId },
      onError: (error) => {
        logger.error({ error })
      }
    })

    if (data?.userProfile?.migrationMetadata?.migrationStatus === ProfileMigrationStatus.Required) {
      try {
        if (useNewMigrationMutation) {
          userMigrateMutation()
        } else {
          const token = await getAuth().currentUser?.getIdToken()
          if (!token) {
            logger.error('Unable to fetch idToken for migration')
          } else {
            legacyLoginMutation({
              variables: {
                token
              }
            })
          }
        }

        logger.info(`${useNewMigrationMutation ? '[NEW]' : '[LEGACY]'} Dormant user migration started, userId: ${data?.userProfile?.id}`)
      } catch (ex) {
        logger.error(`${useNewMigrationMutation ? '[NEW]' : '[LEGACY]'} Dormant user migration failed to start, userId: ${data?.userProfile?.id}`, ex)
      }
    } else if (data?.userProfile?.migrationMetadata?.hasKafkaBucket && onDemandConversionEnabled) {
      const kafkaBucketId = data?.bucketsByUserId?.items?.find(
        (bucket) => bucket.bucketType === BucketType.Mybucket && bucket.isConverted === false
      )?.id

      if (kafkaBucketId) {
        try {
          await userConvertMutation()

          logger.info(`Bucket conversion started for userId: ${data?.userProfile?.id}`)
        } catch (err) {
          logger.error(`Bucket conversion failed to start for userId: ${data?.userProfile?.id}`, err)
        }
      }
    }
  }

  const declineAndCancel = () => {
    if (loading) return
    mutation({
      variables: { data: { termsAccepted: false } },
      onCompleted() {
        startMigrationOrConversion()
        window.location.href = 'https://my.photobucket.com/photobucket-account-deletion-step1'
      }
    })
  }

  const accept = () => {
    if (loading) return
    mutation({
      variables: { data: { termsAccepted: true } },
      onCompleted() {
        attemptRedirect()
        modalVar(null)
      }
    })
  }

  return (
    <Dialog data-test="invite-people-dialog" open={modal === 'termsAndConditionsLogin'}>
      <DialogTitle data-test="invite-users-title" sx={{ textAlign: 'center', padding: { xs: '10px', sm: '20px 24px' } }}>
        We&apos;ve Updated Our Policies
      </DialogTitle>
      <DialogContent sx={{ padding: { xs: '20px 10px', sm: '20px 24px' } }}>
        <Typography>
          Please agree to our{' '}
          <Link to="https://photobucket.com/legal/terms-of-service" target="_blank" data-test="terms-tou">
            Terms of Use
          </Link>
          ,{' '}
          <Link to="https://photobucket.com/legal/privacy-policy" target="_blank" data-test="terms-privacy">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="https://photobucket.com/legal/biometric-information-privacy-policy" target="_blank" data-test="terms-biometric">
            Biometric Information Privacy Policy
          </Link>{' '}
          before continuing.
        </Typography>
      </DialogContent>
      {showCancel && (
        <DialogContent sx={{ padding: { xs: '20px 10px', sm: '20px 24px' } }}>
          <Typography>
            If you do not agree to our updated policies, you will need to delete your account. If you need support, you can{' '}
            <Link to="https://support.photobucket.com/hc/en-us" target="_blank" data-test="terms-contact">
              contact our support team.
            </Link>
          </Typography>
        </DialogContent>
      )}
      <DialogActions sx={{ mx: 2, mb: 1, justifyContent: 'space-between' }}>
        {!showCancel && (
          <Button color="error" variant="text" onClick={decline} disabled={loading} data-test="terms-disagree">
            I Do Not Agree
          </Button>
        )}
        {showCancel && (
          <Button color="error" variant="text" onClick={declineAndCancel} disabled={loading} data-test="terms-delete">
            I Do Not Agree, Delete My Account
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          onClick={accept}
          disabled={loading}
          data-test={showCancel ? 'terms-agree-second' : 'terms-agree-first'}
        >
          I Agree
        </Button>
      </DialogActions>
    </Dialog>
  )
}
